import axios, { AxiosInstance } from 'axios';
import { 
  RefreshTokenApiResponse, 
  ChangePasswordRequest, 
  ChangePasswordApiResponse,
  UpdateProfileRequest,
  UpdateProfileApiResponse,
  AuthApiResponse,
  RegisterRequest,
  RegisterApiResponse,
  BaseApiResponse,
  User,
  AuctionsResponse,
  CitiesResponse,
  DistrictsResponse,
  AuctionListRequest,
  AuctionListResponse,
  AuctionDetailResponse,
  PendingDepositAuctionsResponse,
  UserBidsResponse,
  UserFavoritesRequest,
  UserFavoritesResponse,
  AddFavoriteRequest,
  AddFavoriteApiResponse,
  RemoveFavoriteRequest,
  RemoveFavoriteApiResponse,
  RequestFormData,
  RequestFormApiResponse,
  MyRequestFormsRequest,
  MyRequestFormsResponse,
  AdminRequestFormsRequest,
  AdminRequestFormsResponse,
  SendSmsCodeRequest,
  SendSmsCodeApiResponse,
  VerifySmsCodeRequest,
  VerifySmsCodeApiResponse,
  CreateAuctionAdminRequest,
  CreateAuctionAdminApiResponse,
  UpdateAuctionAdminRequest,
  UpdateAuctionAdminApiResponse,
  ReactivateAuctionRequest,
  ReactivateAuctionApiResponse,
  AddAuctionImagesApiResponse,
  AddAuctionVideoApiResponse,
  RedirectEidsApiResponse,
  VerifyEidsRequest,
  VerifyEidsApiResponse,
  AdminAuctionDetailResponse,
  GetUsersAdminRequest,
  GetUsersAdminResponse
} from './types/api';
import { clearTokenValidationCache } from './jwt-utils';
import { API_CONFIG } from './config';

// Helper function to get User-Agent for API routes
export const getUserAgent = (request?: Request): string => {
  if (request) {
    // Try to get User-Agent from incoming request
    const incomingUserAgent = request.headers.get('user-agent');
    if (incomingUserAgent) {
      return incomingUserAgent;
    }
  }
  
  // Fallback to default User-Agent
  return 'TrinkEmlak-API/1.0.0';
};

// Check if we're on the server side
const isServer = typeof window === 'undefined';

// API Configuration
// Server-side'da API_CONFIG.BASE_URL kullan, client-side'da proxy kullan
const API_BASE_URL = isServer 
  ? API_CONFIG.BASE_URL
  : ''; // Client-side'da proxy kullan

// Helper function to get current locale
const getCurrentLocale = (): string | null => {
  if (isServer) return null;
  
  // Try to get locale from URL path
  const pathname = window.location.pathname;
  const localeMatch = pathname.match(/^\/([a-z]{2})(\/|$)/);
  
  if (localeMatch) {
    return localeMatch[1];
  }
  
  // Fallback to default locale
  return 'tr';
};

// Create axios instance for server-side
const createServerApiClient = (locale?: string): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    withCredentials: true, // HttpOnly cookie'leri otomatik gönder
    proxy: false, // Proxy kontrolünü devre dışı bırak (url.parse() deprecation uyarısını önlemek için)
    headers: {
      'Content-Type': 'application/json',
      'User-Agent': 'TrinkEmlak-Server/1.0.0',
    },
  });

  // Request interceptor for server-side
  client.interceptors.request.use(
    async (config) => {
      // Add language header based on provided locale
      if (locale) {
        config.headers['X-Language'] = locale;
      }
      
      // Server-side'da cookie'leri manuel olarak Authorization header'ına ekle
      // Next.js server'dan backend'e yapılan isteklerde cookie'ler otomatik gitmez
      try {
        const { cookies } = await import('next/headers');
        const cookieStore = await cookies();
        const token = cookieStore.get('trink_access_token')?.value;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        // Error getting server-side token
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  return client;
};

// Helper function to make fetch requests with automatic retry on 401
const fetchWithRetry = async (url: string, options: RequestInit & { _retry?: boolean } = {}): Promise<Response> => {
  const currentLocale = getCurrentLocale();
  
  // Get User-Agent from browser
  const userAgent = typeof window !== 'undefined' ? navigator.userAgent : 'TrinkEmlak-Web/1.0.0';
  
  const defaultOptions: RequestInit = {
    credentials: 'include', // HttpOnly cookie'leri otomatik gönder
    headers: {
      'Content-Type': 'application/json',
      'User-Agent': userAgent,
      ...(currentLocale && { 'X-Language': currentLocale }),
      ...options.headers,
    },
  };

  const mergedOptions = { ...defaultOptions, ...options };

  const response = await fetch(url, mergedOptions);

  // Handle 401 and 403 errors with token refresh
  if ((response.status === 401 || response.status === 403) && !options._retry) {
    try {
      // Try to refresh token
      const refreshResponse = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!refreshResponse.ok) {
        // Refresh failed, redirect to login
        if (typeof window !== 'undefined') {
          const currentLocale = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/)?.[1] || 'tr';
          window.location.href = `/${currentLocale}/giris`;
        }
        throw new Error('Token refresh failed');
      }

      const refreshData = await refreshResponse.json();

      if (refreshData.isSuccess) {
        // Token validation cache'ini temizle
        clearTokenValidationCache();
        
        // Retry original request
        return fetchWithRetry(url, { ...options, _retry: true });
      } else {
        // Refresh failed, redirect to login
        if (typeof window !== 'undefined') {
          const currentLocale = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/)?.[1] || 'tr';
          window.location.href = `/${currentLocale}/giris`;
        }
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      // Refresh failed, redirect to login
      if (typeof window !== 'undefined') {
        const currentLocale = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/)?.[1] || 'tr';
        window.location.href = `/${currentLocale}/login`;
      }
      throw error;
    }
  }

  return response;
};

// Export the API client instance (only server-side now)
export const apiClient = isServer ? createServerApiClient() : null;

// Server-side API client factory
export const createServerApiClientWithLocale = (locale: string) => {
  return createServerApiClient(locale);
};

// Helper function to parse API errors
export const parseApiError = (error: unknown, defaultMessage: string = 'Request failed'): { message: string; errors: string[] } => {
  // Axios error response handling
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response?: { data?: unknown; status?: number } };
    
    // Response data'dan hata mesajını al
    let errorMessage = defaultMessage;
    let errors: string[] = [];
    
    if (axiosError.response?.data) {
      const responseData = axiosError.response.data;
      
      // Farklı hata formatlarını kontrol et
      if (typeof responseData === 'string') {
        errorMessage = responseData;
      } else if (typeof responseData === 'object' && responseData !== null) {
        const responseObj = responseData as { message?: string; errors?: string[] };
        errorMessage = responseObj.message || defaultMessage;
        errors = responseObj.errors ||  [];
      }
    }
    
    // HTTP status koduna göre özel mesajlar
    if (axiosError.response?.status) {
      switch (axiosError.response.status) {
        case 400:
          errorMessage = errorMessage || 'Invalid request data';
          break;
        case 401:
          errorMessage = errorMessage || 'Authentication required';
          break;
        case 403:
          errorMessage = errorMessage || 'Access denied';
          break;
        case 404:
          errorMessage = errorMessage || 'Resource not found';
          break;
        case 422:
          errorMessage = errorMessage || 'Validation failed';
          break;
        case 500:
          errorMessage = errorMessage || 'Server error';
          break;
      }
    }
    
    return {
      message: errorMessage,
      errors: Array.isArray(errors) ? errors : [errors].filter(Boolean),
    };
  } else {
    // Network or other error
    const errorMessage = error instanceof Error ? error.message : defaultMessage;
    return {
      message: errorMessage,
      errors: [],
    };
  }
};

// API service functions
export const apiService = {
  // Authentication - HttpOnly cookie'ler kullanıyor
  auth: {
    // Login - Next.js API route kullanarak HttpOnly cookie set eder
    login: async (credentials: { phoneNumber: string; password: string; rememberMe?: boolean }): Promise<AuthApiResponse> => {
      // Client-side'dan Next.js API route'una istek yap
      // Bu route backend'e istek yapıp HttpOnly cookie set eder
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });
      
      const data = await response.json();
      return data;
    },

    // Register - Next.js API route kullanarak HttpOnly cookie set eder
    register: async (userData: RegisterRequest): Promise<RegisterApiResponse> => {
      // Client-side'dan Next.js API route'una istek yap
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
      
      const data = await response.json();
      return data;
    },

    // Logout - Next.js API route kullanarak HttpOnly cookie'leri temizler
    logout: async (): Promise<BaseApiResponse<null>> => {
      // Client-side'dan Next.js API route'una istek yap
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      return data;
    },

    // Me - Next.js API route kullanarak kullanıcı bilgisi al
    me: async (): Promise<BaseApiResponse<User>> => {
      const response = await fetchWithRetry('/api/auth/me');
      return response.json();
    },

    // Refresh - Next.js API route kullanarak HttpOnly cookie'leri günceller
    refresh: async (): Promise<RefreshTokenApiResponse> => {
      // Client-side'dan Next.js API route'una istek yap
      // RefreshToken HttpOnly cookie'den okunacak
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      return data;
    },

    // Change Password - Next.js API route kullanarak şifre değiştir
    changePassword: async (passwordData: ChangePasswordRequest): Promise<ChangePasswordApiResponse> => {
      const response = await fetchWithRetry('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify(passwordData),
      });
      return response.json();
    },

    // EIDS Redirect - Backend'den URL alıp E-Devlet'e yönlendirme
    redirectToEids: async (): Promise<void> => {
      // Backend API'ye istek at - Backend rate limiting kontrolü yapacak
      const response = await fetchWithRetry('/api/auth/eids/redirect', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data: RedirectEidsApiResponse = await response.json();

      // Response ve data kontrolü
      if (!response.ok || !data.isSuccess || !data.data?.redirectUrl) {
        throw new Error(data.message || 'E-Devlet yönlendirme hatası');
      }

      // Client-side'da yönlendir (window.location CORS kontrolü yapmaz)
      window.location.href = data.data.redirectUrl;
    },

    // EIDS Verify - Yetki kodu ile doğrulama
    verifyEids: async (request: VerifyEidsRequest): Promise<VerifyEidsApiResponse> => {
      const response = await fetchWithRetry('/api/auth/eids/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });
      return response.json();
    },
  },

  // User Profile
  user: {
    updateProfile: async (profileData: UpdateProfileRequest): Promise<UpdateProfileApiResponse> => {
      const response = await fetchWithRetry('/api/users/update-profile', {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });
      
      const data = await response.json();
      
      // Profil güncelleme başarılı olduktan sonra token'ı yenile
      // HttpOnly cookie kullanıldığı için refresh API route'unu kullan
      if (data.isSuccess) {
        try {
          // HttpOnly cookie'li refresh endpoint'ini kullan
          const refreshResponse = await apiService.auth.refresh();
          
          if (refreshResponse.isSuccess) {
            // Token validation cache'ini temizle
            clearTokenValidationCache();
            
            // Token yenileme başarılı olduğunu belirtmek için response'a ek bilgi ekle
            data.tokenRefreshed = true;
          }
        } catch (error) {
          // Token yenileme başarısız olsa bile profil güncelleme başarılı sayılır
        }
      }
      
      return data;
    },
    // Admin - Kullanıcı listesi
    getUsersAdmin: async (
      pageNumber: number,
      pageSize: number,
      filters: GetUsersAdminRequest
    ): Promise<GetUsersAdminResponse> => {
      const queryParams = new URLSearchParams({
        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await fetchWithRetry(`/api/users/get-users-admin?${queryParams.toString()}`, {
        method: 'POST',
        body: JSON.stringify(filters),
      });
      
      return response.json();
    },
  },

  // Auctions
  auctions: {
    getActive: async (): Promise<AuctionsResponse> => {
      const response = await fetchWithRetry(API_CONFIG.ENDPOINTS.AUCTIONS.ACTIVE);
      return response.json();
    },
    getUpcoming: async (): Promise<AuctionsResponse> => {
      const response = await fetchWithRetry(API_CONFIG.ENDPOINTS.AUCTIONS.UPCOMING);
      return response.json();
    },
    getCompleted: async (): Promise<AuctionsResponse> => {
      const response = await fetchWithRetry(API_CONFIG.ENDPOINTS.AUCTIONS.COMPLETED);
      return response.json();
    },
    // Yeni müzayede listesi servisi - Next.js API route kullanarak
    getList: async (params: AuctionListRequest): Promise<AuctionListResponse> => {
      const { pageNumber, pageSize, isActive, searchTerm, ...filters } = params;
      
      const queryParams = new URLSearchParams({
        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString()
      });

      // isActive ve searchTerm parametrelerini request body'ye ekle
      const requestBody = {
        ...filters,
        isActive: isActive,
        searchTerm: searchTerm
      };

      const fullUrl = `/api/auctions/list?${queryParams.toString()}`;

      const response = await fetchWithRetry(fullUrl, {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });
      
      const responseData = await response.json();
      
      return responseData;
    },
    // Müzayede detay servisi - slug ile
    getBySlug: async (slug: string): Promise<AuctionDetailResponse> => {
      const response = await fetchWithRetry(API_CONFIG.ENDPOINTS.AUCTIONS.BY_SLUG(slug));
      return response.json();
    },
    // Admin - Yeni müzayede oluşturma servisi
    createAuctionAdmin: async (auctionData: CreateAuctionAdminRequest): Promise<CreateAuctionAdminApiResponse> => {
      const response = await fetchWithRetry('/api/auctions/create-auction-admin', {
        method: 'POST',
        body: JSON.stringify(auctionData),
      });
      return response.json();
    },
    // Admin - Müzayede güncelleme servisi
    updateAuctionAdmin: async (auctionId: string, auctionData: UpdateAuctionAdminRequest): Promise<UpdateAuctionAdminApiResponse> => {
      const response = await fetchWithRetry(`/api/auctions/update-auction-admin/${auctionId}`, {
        method: 'PUT',
        body: JSON.stringify(auctionData),
      });
      return response.json();
    },
    // Admin - Müzayede resimleri ekleme servisi
    addAuctionImages: async (auctionId: string, imageFiles: File[]): Promise<AddAuctionImagesApiResponse> => {
      const formData = new FormData();
      formData.append('auctionId', auctionId);
      
      // Her resim dosyasını formData'ya ekle
      imageFiles.forEach((file, index) => {
        formData.append('imageFiles', file);
      });

      const response = await fetchWithRetry('/api/auctions/add-auction-images-admin', {
        method: 'POST',
        body: formData,
        // Content-Type header'ını otomatik olarak ayarlamak için kaldırıyoruz
        headers: {
          // Authorization header'ı fetchWithRetry içinde otomatik ekleniyor
        }
      });
      return response.json();
    },
    // Admin - Müzayede resmi silme servisi
    deleteAuctionImage: async (imageUrl: string): Promise<BaseApiResponse<null>> => {
      // URL'yi encode ederek query string olarak gönderiyoruz
      const encodedUrl = encodeURIComponent(imageUrl);
      const response = await fetchWithRetry(`/api/auctions/delete-auction-image-admin?imageUrl=${encodedUrl}`, {
        method: 'DELETE',
      });
      return response.json();
    },
    // Admin - Müzayede videosu silme servisi
    deleteAuctionVideo: async (auctionId: string): Promise<BaseApiResponse<null>> => {
      const response = await fetchWithRetry(`/api/auctions/delete-auction-video-admin/${auctionId}`, {
        method: 'DELETE',
      });
      return response.json();
    },
    // Admin - Müzayede videosu ekleme servisi
    addAuctionVideo: async (auctionId: string, videoFile: File): Promise<AddAuctionVideoApiResponse> => {
      const formData = new FormData();
      formData.append('auctionId', auctionId);
      formData.append('videoFile', videoFile);

      const response = await fetchWithRetry('/api/auctions/add-auction-video-admin', {
        method: 'POST',
        body: formData,
        headers: {
          // Authorization header'ı fetchWithRetry içinde otomatik ekleniyor
        }
      });
      return response.json();
    },
    // Admin - Müzayede aktifleştirme servisi
    activateAuction: async (auctionId: string): Promise<BaseApiResponse<null>> => {
      const response = await fetchWithRetry(`/api/auctions/activate/${auctionId}`, {
        method: 'PUT',
      });
      return response.json();
    },
    // Admin - Müzayede pasife alma servisi
    deactivateAuction: async (auctionId: string): Promise<BaseApiResponse<null>> => {
      const response = await fetchWithRetry(`/api/auctions/deactivate/${auctionId}`, {
        method: 'PUT',
      });
      return response.json();
    },
    // Admin - Müzayede yeniden yayınlama servisi
    reactivateAuction: async (reactivateData: ReactivateAuctionRequest): Promise<ReactivateAuctionApiResponse> => {
      const response = await fetchWithRetry('/api/auctions/reactivate', {
        method: 'POST',
        body: JSON.stringify(reactivateData),
      });
      return response.json();
    },
    // Admin - Müzayede detay servisi (ID ile)
    getByIdAdmin: async (auctionId: string): Promise<AdminAuctionDetailResponse> => {
      const response = await fetchWithRetry(`/api/auctions/admin/${auctionId}`);
      return response.json();
    },
  },

  // Deposits
  deposits: {
    getPendingDepositAuctions: async (): Promise<PendingDepositAuctionsResponse> => {
      const response = await fetchWithRetry('/api/deposits/pending-deposit-auctions');
      return response.json();
    },
  },

  // User Bids
  bids: {
    getUserBids: async (): Promise<UserBidsResponse> => {
      const response = await fetchWithRetry('/api/bids/my-bids');
      return response.json();
    },
  },

  // User Favorites
  favorites: {
    getUserFavorites: async (params: UserFavoritesRequest): Promise<UserFavoritesResponse> => {
      const queryParams = new URLSearchParams({
        pageNumber: params.pageNumber.toString(),
        pageSize: params.pageSize.toString()
      });
      const response = await fetchWithRetry(`/api/auctionfavorites/user/favorites?${queryParams}`);
      return response.json();
    },
    addFavorite: async (params: AddFavoriteRequest): Promise<AddFavoriteApiResponse> => {
      const response = await fetchWithRetry('/api/auctionfavorites', {
        method: 'POST',
        body: JSON.stringify({ 
          auctionId: params.auctionId
        }),
      });
      return response.json();
    },
    removeFavorite: async (params: RemoveFavoriteRequest): Promise<RemoveFavoriteApiResponse> => {
      const response = await fetchWithRetry('/api/auctionfavorites', {
        method: 'DELETE',
        body: JSON.stringify({ 
          auctionId: params.auctionId
        }),
      });
      return response.json();
    },
  },

  // Cities
  cities: {
    getAll: async (): Promise<CitiesResponse> => {
      const response = await fetchWithRetry(API_CONFIG.ENDPOINTS.CITIES.ALL);
      return response.json();
    },
  },

  // Districts
  districts: {
    getByCityIds: async (cityIds: string[]): Promise<DistrictsResponse> => {
      const queryParams = new URLSearchParams();
      cityIds.forEach(cityId => {
        queryParams.append('cityIds', cityId);
      });
      const response = await fetchWithRetry(`${API_CONFIG.ENDPOINTS.DISTRICTS.BY_CITY_IDS}?${queryParams.toString()}`);
      return response.json();
    },
    getByCityId: async (cityId: string): Promise<DistrictsResponse> => {
      const response = await fetchWithRetry(`${API_CONFIG.ENDPOINTS.DISTRICTS.BY_CITY_IDS}?cityIds=${cityId}`);
      return response.json();
    },
  },

  // Request Forms
  requestForms: {
    create: async (formData: RequestFormData): Promise<RequestFormApiResponse> => {
      // FormData oluştur
      const form = new FormData();
      
      // API'ye göre alanları ekle
      form.append('realEstateType', formData.realEstateType.toString());
      form.append('cityId', formData.cityId);
      form.append('districtId', formData.districtId);
      
      // Sadece konut için yapım yılı ekle
      if (formData.buildingYear) {
        form.append('buildingYear', formData.buildingYear);
      }
      
      form.append('address', formData.address);
      form.append('deedType', formData.deedType);
      form.append('hasMortgage', formData.hasMortgage.toString());
      
      // İpotek miktarını sadece ipotek varsa ekle
      if (formData.hasMortgage && formData.mortgageAmount) {
        form.append('mortgageAmount', formData.mortgageAmount);
      }
      
      form.append('minimumPrice', formData.minimumPrice);
      form.append('description', formData.description);
      form.append('deedImage', formData.deedImage);

      const response = await fetchWithRetry('/api/requestforms', {
        method: 'POST',
        body: form,
        headers: {
          // Content-Type'ı fetch otomatik ayarlayacak
        },
      });
      return response.json();
    },
    // Kullanıcının kendi talep formlarını getir
    getMyRequestForms: async (params: MyRequestFormsRequest): Promise<MyRequestFormsResponse> => {
      const queryParams = new URLSearchParams({
        pageNumber: params.pageNumber.toString(),
        pageSize: params.pageSize.toString()
      });
      
      if (params.status !== undefined) {
        queryParams.append('status', params.status.toString());
      }
      
      const response = await fetchWithRetry(`/api/requestforms/my-requests?${queryParams.toString()}`);
      return response.json();
    },
    // Admin endpoint - Tüm talep formlarını getir
    getAdminRequestForms: async (params: AdminRequestFormsRequest): Promise<AdminRequestFormsResponse> => {
      const queryParams = new URLSearchParams({
        pageNumber: params.pageNumber.toString(),
        pageSize: params.pageSize.toString()
      });
      
      if (params.status !== undefined) {
        queryParams.append('status', params.status.toString());
      }
      
      const response = await fetchWithRetry(`/api/requestforms/admin?${queryParams.toString()}`);
      return response.json();
    },

    updateStatus: async (formId: string, status: number): Promise<BaseApiResponse<null>> => {
      const response = await fetchWithRetry(`/api/requestforms/${formId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }), // JSON formatında gönder
      });
      return response.json();
    },
  },

  // SMS Code Services
  smsCode: {
    // SMS kodu gönder
    send: async (request: SendSmsCodeRequest): Promise<SendSmsCodeApiResponse> => {
      const response = await fetchWithRetry('/api/smscode/send', {
        method: 'POST',
        body: JSON.stringify(request),
      });
      return response.json();
    },

    // SMS kodu doğrula
    verify: async (request: VerifySmsCodeRequest): Promise<VerifySmsCodeApiResponse> => {
      const response = await fetchWithRetry('/api/smscode/verify', {
        method: 'POST',
        body: JSON.stringify(request),
      });
      return response.json();
    },
  },
};

// Server-side API service functions
export const createServerApiService = (locale: string) => {
  const serverClient = createServerApiClientWithLocale(locale);
  
  return {
    cities: {
      getAll: async (): Promise<CitiesResponse> => {
        try {
          const response = await serverClient.get(API_CONFIG.ENDPOINTS.CITIES.ALL);
          return response.data;
        } catch (error) {
          // Hata durumunda boş array döndür
          return {
            isSuccess: false,
            statusCode: 500,
            data: [],
            message: 'Failed to fetch cities',
            errors: [],
            timeStamp: new Date().toISOString(),
            apiVersion: '1.0'
          };
        }
      },
    },
    auctions: {
      getActive: async (): Promise<AuctionsResponse> => {
        try {
          const response = await serverClient.get(API_CONFIG.ENDPOINTS.AUCTIONS.ACTIVE);
          return response.data;
        } catch (error) {
          // Hata durumunda boş array döndür
          return {
            isSuccess: false,
            statusCode: 500,
            data: [],
            message: 'Failed to fetch active auctions',
            errors: [],
            timeStamp: new Date().toISOString(),
            apiVersion: '1.0'
          };
        }
      },
      getUpcoming: async (): Promise<AuctionsResponse> => {
        try {
          const response = await serverClient.get(API_CONFIG.ENDPOINTS.AUCTIONS.UPCOMING);
          return response.data;
        } catch (error) {
          // Hata durumunda boş array döndür
          return {
            isSuccess: false,
            statusCode: 500,
            data: [],
            message: 'Failed to fetch upcoming auctions',
            errors: [],
            timeStamp: new Date().toISOString(),
            apiVersion: '1.0'
          };
        }
      },
      getCompleted: async (): Promise<AuctionsResponse> => {
        try {
          const response = await serverClient.get(API_CONFIG.ENDPOINTS.AUCTIONS.COMPLETED);
          return response.data;
        } catch (error) {
          // Hata durumunda boş array döndür
          return {
            isSuccess: false,
            statusCode: 500,
            data: [],
            message: 'Failed to fetch completed auctions',
            errors: [],
            timeStamp: new Date().toISOString(),
            apiVersion: '1.0'
          };
        }
      },
      // Yeni müzayede listesi servisi - server-side
      getList: async (params: AuctionListRequest): Promise<AuctionListResponse> => {
        try {
          const { pageNumber, pageSize, isActive, searchTerm, ...filters } = params;
          
          const queryParams = new URLSearchParams({
            pageNumber: pageNumber.toString(),
            pageSize: pageSize.toString()
          });

          // searchTerm parametresi varsa query string'e ekle
          if (searchTerm && searchTerm.trim()) {
            queryParams.append('searchTerm', searchTerm.trim());
          }

          // isActive parametresini request body'ye ekle
          const requestBody = {
            ...filters,
            isActive: isActive
          };

          const fullUrl = `${API_CONFIG.ENDPOINTS.AUCTIONS.LIST}?${queryParams.toString()}`;

          const response = await serverClient.post(`${API_CONFIG.ENDPOINTS.AUCTIONS.LIST}?${queryParams.toString()}`, requestBody);
          
          return response.data;
        } catch (error) {
          // Hata durumunda boş paginated response döndür
          return {
            isSuccess: false,
            statusCode: 500,
            data: {
              data: [], // items yerine data
              pageIndex: params.pageNumber, // pageNumber yerine pageIndex
              pageSize: params.pageSize,
              totalCount: 0,
              totalPages: 0,
              hasPreviousPage: false,
              hasNextPage: false
            },
            message: 'Failed to fetch auction list',
            errors: [],
            timeStamp: new Date().toISOString(),
            apiVersion: '1.0'
          };
        }
      },
      // Müzayede detay servisi - slug ile (server-side)
      getBySlug: async (slug: string): Promise<AuctionDetailResponse> => {
        try {
          const response = await serverClient.get(API_CONFIG.ENDPOINTS.AUCTIONS.BY_SLUG(slug));
          return response.data;
        } catch (error) {
          // Hata durumunda boş response döndür
          return {
            isSuccess: false,
            statusCode: 500,
            data: null,
            message: 'Failed to fetch auction detail',
            errors: [],
            timeStamp: new Date().toISOString(),
            apiVersion: '1.0'
          };
        }
      },
    },
    deposits: {
      getPendingDepositAuctions: async (): Promise<PendingDepositAuctionsResponse> => {
        try {
          const response = await serverClient.get(API_CONFIG.ENDPOINTS.DEPOSITS.PENDING_AUCTIONS);
          return response.data;
        } catch (error) {
          // Hata durumunda boş array döndür
          return {
            isSuccess: false,
            statusCode: 500,
            data: [],
            message: 'Failed to fetch pending deposit auctions',
            errors: [],
            timeStamp: new Date().toISOString(),
            apiVersion: '1.0'
          };
        }
      },
    },

    // User Bids
    bids: {
      getUserBids: async (): Promise<UserBidsResponse> => {
        try {
          const response = await serverClient.get(API_CONFIG.ENDPOINTS.BIDS.MY_BIDS);
          return response.data;
        } catch (error) {
          // Hata durumunda boş array döndür
          return {
            isSuccess: false,
            statusCode: 500,
            data: [],
            message: 'Failed to fetch user bids',
            errors: [],
            timeStamp: new Date().toISOString(),
            apiVersion: '1.0'
          };
        }
      },
    },

    // User Favorites
    favorites: {
      getUserFavorites: async (params: UserFavoritesRequest): Promise<UserFavoritesResponse> => {
        try {
          const queryParams = new URLSearchParams({
            pageNumber: params.pageNumber.toString(),
            pageSize: params.pageSize.toString()
          });
          const response = await serverClient.get(`${API_CONFIG.ENDPOINTS.AUCTION.USER_FAVORITES}?${queryParams}`);
          return response.data;
        } catch (error) {
          // Hata durumunda boş paginated response döndür
          return {
            isSuccess: false,
            statusCode: 500,
            data: {
              data: [],
              pageIndex: 0,
              pageSize: params.pageSize,
              totalCount: 0,
              totalPages: 0,
              hasPreviousPage: false,
              hasNextPage: false
            },
            message: 'Failed to fetch user favorites',
            errors: [],
            timeStamp: new Date().toISOString(),
            apiVersion: '1.0'
          };
        }
      },
      addFavorite: async (params: AddFavoriteRequest): Promise<AddFavoriteApiResponse> => {
        try {
          const response = await serverClient.post(API_CONFIG.ENDPOINTS.AUCTION.FAVORITES, { 
            auctionId: params.auctionId, 
            action: 'add' 
          });
          return response.data;
        } catch (error) {
          return {
            isSuccess: false,
            statusCode: 500,
            data: null,
            message: 'Failed to add favorite',
            errors: [],
            timeStamp: new Date().toISOString(),
            apiVersion: '1.0'
          };
        }
      },
      removeFavorite: async (params: RemoveFavoriteRequest): Promise<RemoveFavoriteApiResponse> => {
        try {
          const response = await serverClient.delete(API_CONFIG.ENDPOINTS.AUCTION.FAVORITES, { 
            data: { 
              auctionId: params.auctionId
            }
          });
          return response.data;
        } catch (error) {
          return {
            isSuccess: false,
            statusCode: 500,
            data: null,
            message: 'Failed to remove favorite',
            errors: [],
            timeStamp: new Date().toISOString(),
            apiVersion: '1.0'
          };
        }
      },
    },
    districts: {
      getByCityIds: async (cityIds: string[]): Promise<DistrictsResponse> => {
        try {
          const queryParams = new URLSearchParams();
          cityIds.forEach(cityId => {
            queryParams.append('cityIds', cityId);
          });
          const response = await serverClient.get(`${API_CONFIG.ENDPOINTS.DISTRICTS.BY_CITY_IDS}?${queryParams.toString()}`);
          return response.data;
        } catch (error) {
          // Hata durumunda boş array döndür
          return {
            isSuccess: false,
            statusCode: 500,
            data: [],
            message: 'Failed to fetch districts',
            errors: [],
            timeStamp: new Date().toISOString(),
            apiVersion: '1.0'
          };
        }
      },
      getByCityId: async (cityId: string): Promise<DistrictsResponse> => {
        try {
          const response = await serverClient.get(`${API_CONFIG.ENDPOINTS.DISTRICTS.BY_CITY_IDS}?cityIds=${cityId}`);
          return response.data;
        } catch (error) {
          // Hata durumunda boş array döndür
          return {
            isSuccess: false,
            statusCode: 500,
            data: [],
            message: 'Failed to fetch districts',
            errors: [],
            timeStamp: new Date().toISOString(),
            apiVersion: '1.0'
          };
        }
      },
    },
  };
};

export default apiClient;
